import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TablePagination, Button, Divider, CircularProgress, Chip,
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip,
} from '@mui/material';
import { ArrowBack, Block, Replay, CheckCircle, Cancel, AccessTime } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from '../DashboardLayout';
import PageLoader from '../../../../Reusable/PageLoader';
import baseURL from '../../config';

const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
    orange: '#FF8C00',
};

const headerCellSx = { fontWeight: 'bold', fontSize: '0.85rem', whiteSpace: 'nowrap', color: MS_COLORS.text };
const bodyCellSx = { fontSize: '0.8rem', color: MS_COLORS.textSecondary, whiteSpace: 'nowrap' };

const fmt = (date) => date ? new Date(date).toLocaleDateString('en-GB') : '—';

const InfoRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${MS_COLORS.border}` }}>
        <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: MS_COLORS.text, textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>{value}</Typography>
    </Box>
);

const statusBadge = (status) => {
    const map = {
        Completed: { color: MS_COLORS.accent,   bg: '#EBF5EB', icon: <CheckCircle fontSize="small" /> },
        Pending:   { color: MS_COLORS.orange,    bg: '#FFF4E5', icon: <AccessTime fontSize="small" /> },
        Failed:    { color: MS_COLORS.red,       bg: '#FEF0EB', icon: <Cancel fontSize="small" /> },
    };
    const s = map[status] || { color: MS_COLORS.text, bg: '#F3F2F1', icon: null };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: s.color, fontWeight: 'bold', fontSize: '0.8rem' }}>
            {s.icon}
            <Typography variant="body2" sx={{ fontWeight: 'inherit', fontSize: 'inherit', color: 'inherit' }}>{status}</Typography>
        </Box>
    );
};

const subStatusChip = (status) => {
    const map = {
        active:    { label: 'Active',    color: MS_COLORS.accent, bg: '#EBF5EB' },
        cancelled: { label: 'Cancelled', color: MS_COLORS.textSecondary, bg: '#F3F2F1' },
        failed:    { label: 'Failed',    color: MS_COLORS.red,    bg: '#FEF0EB' },
    };
    const s = map[status] || { label: status, color: MS_COLORS.text, bg: '#F3F2F1' };
    return <Chip label={s.label} size="small" sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 'bold', fontSize: '0.72rem', height: 22 }} />;
};

const RecurringDonationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [subscription, setSubscription] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Mark-failed dialog
    const [markFailedOpen, setMarkFailedOpen] = useState(false);
    const [markFailedLoading, setMarkFailedLoading] = useState(false);

    // Retry dialog
    const [retryOpen, setRetryOpen] = useState(false);
    const [retryLoading, setRetryLoading] = useState(false);

    // Unsubscribe dialog
    const [unsubOpen, setUnsubOpen] = useState(false);
    const [unsubLoading, setUnsubLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${baseURL}/admin/recurring-subscriptions/${id}/donations`, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Failed to fetch subscription details');
            const data = await res.json();
            setSubscription(data.subscription);
            setTransactions(data.transactions);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleMarkFailed = async () => {
        setMarkFailedLoading(true);
        try {
            const res = await fetch(`${baseURL}/admin/recurring-subscriptions/${id}/mark-failed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to mark transaction');
            toast.success(data.message);
            setMarkFailedOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.message || 'An error occurred');
        } finally {
            setMarkFailedLoading(false);
        }
    };

    const handleRetry = async () => {
        setRetryLoading(true);
        try {
            const res = await fetch(`${baseURL}/admin/recurring-subscriptions/${id}/retry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Retry failed');
            toast.success(data.message);
            setRetryOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.message || 'An error occurred');
        } finally {
            setRetryLoading(false);
        }
    };

    const handleUnsubscribe = async () => {
        setUnsubLoading(true);
        try {
            const res = await fetch(`${baseURL}/admin/recurring-subscriptions/${id}/unsubscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Unsubscribe failed');
            toast.success('Subscription cancelled. Notifications sent.');
            setUnsubOpen(false);
            navigate('/admin/recurring-donations');
        } catch (err) {
            toast.error(err.message || 'An error occurred');
        } finally {
            setUnsubLoading(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <PageLoader message="Loading subscription details..." />
            </DashboardLayout>
        );
    }

    if (error || !subscription) {
        return (
            <DashboardLayout>
                <Box sx={{ p: 3 }}>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2, textTransform: 'none', color: MS_COLORS.primary }}>
                        Back
                    </Button>
                    <Typography color="error">{error || 'Subscription not found.'}</Typography>
                </Box>
            </DashboardLayout>
        );
    }

    const mostRecentTx = transactions[0] || null;

    return (
        <DashboardLayout>
            <Box sx={{ mb: 2 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{ textTransform: 'none', color: MS_COLORS.primary, fontSize: '0.875rem' }}
                >
                    Back to Recurring Donations
                </Button>
            </Box>

            {/* Subscription info card */}
            <Box
                sx={{
                    p: 3,
                    backgroundColor: MS_COLORS.background,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: `1px solid ${MS_COLORS.border}`,
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: MS_COLORS.text, flexGrow: 1 }}>
                        Subscription Details
                    </Typography>
                    {subStatusChip(subscription.status)}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 32px' }}>
                    <Box>
                        <InfoRow label="Donor Name" value={subscription.donor_name} />
                        <InfoRow label="Donor Email" value={subscription.donor_email} />
                        {subscription.donor_phone && <InfoRow label="Donor Phone" value={subscription.donor_phone} />}
                        <InfoRow label="Campaign" value={subscription.campaign_name} />
                        <InfoRow label="Amount" value={`UGX ${Number(subscription.amount).toLocaleString()}`} />
                    </Box>
                    <Box>
                        <InfoRow label="Frequency" value={subscription.frequency?.charAt(0).toUpperCase() + subscription.frequency?.slice(1)} />
                        <InfoRow label="Payment Method" value={subscription.payment_method} />
                        <InfoRow label="Subscription Date" value={fmt(subscription.created_at)} />
                        <InfoRow label="Next Charge Date" value={fmt(subscription.next_charge_date)} />
                        <InfoRow label="Failed Attempts" value={subscription.failed_count ?? 0} />
                    </Box>
                </Box>

                {/* Transaction action buttons (on most recent transaction) */}
                {mostRecentTx && subscription.status !== 'cancelled' && (
                    <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {(mostRecentTx.status === 'Pending' || mostRecentTx.status === 'Completed') && (
                            <Tooltip title="Mark most recent transaction as Failed">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Block fontSize="small" />}
                                    onClick={() => setMarkFailedOpen(true)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        borderColor: MS_COLORS.red,
                                        color: MS_COLORS.red,
                                        '&:hover': { backgroundColor: MS_COLORS.red + '10', borderColor: MS_COLORS.red },
                                    }}
                                >
                                    Mark as Failed
                                </Button>
                            </Tooltip>
                        )}
                        {subscription.status === 'failed' && (
                            <Tooltip title="Reset subscription to active">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Replay fontSize="small" />}
                                    onClick={() => setRetryOpen(true)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        borderColor: MS_COLORS.orange,
                                        color: MS_COLORS.orange,
                                        '&:hover': { backgroundColor: MS_COLORS.orange + '10', borderColor: MS_COLORS.orange },
                                    }}
                                >
                                    Retry Subscription
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </Box>

            {/* Donations / Transactions table */}
            <Box
                sx={{
                    p: 2,
                    backgroundColor: MS_COLORS.background,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: `1px solid ${MS_COLORS.border}`,
                    mb: 3,
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: MS_COLORS.text, mb: 2 }}>
                    Donation History
                </Typography>

                {transactions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 3, border: `1px dashed ${MS_COLORS.border}`, borderRadius: 1, bgcolor: MS_COLORS.backgroundLight }}>
                        <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: '0.9rem' }}>
                            No donation transactions recorded for this subscription yet.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 0, overflowX: 'auto', border: `1px solid ${MS_COLORS.border}` }}>
                            <Table sx={{ minWidth: 700 }}>
                                <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                                    <TableRow>
                                        {['Date', 'Amount (UGX)', 'Status', 'Payment Method', 'Reference'].map((h) => (
                                            <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {transactions
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((tx) => (
                                            <TableRow
                                                key={tx.id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                    '&:hover': { backgroundColor: MS_COLORS.hoverBackground },
                                                }}
                                            >
                                                <TableCell sx={bodyCellSx}>{fmt(tx.date)}</TableCell>
                                                <TableCell sx={bodyCellSx}>UGX {Number(tx.amount).toLocaleString()}</TableCell>
                                                <TableCell sx={{ py: 1 }}>{statusBadge(tx.status)}</TableCell>
                                                <TableCell sx={bodyCellSx}>{tx.payment_method || '—'}</TableCell>
                                                <TableCell sx={{ ...bodyCellSx, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {tx.external_reference || '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={transactions.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            sx={{
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions': {
                                    fontSize: '0.8rem',
                                    color: MS_COLORS.textSecondary,
                                },
                            }}
                        />
                    </>
                )}
            </Box>

            {/* Danger zone — Unsubscribe */}
            {subscription.status !== 'cancelled' && (
                <Box
                    sx={{
                        p: 2.5,
                        backgroundColor: MS_COLORS.background,
                        borderRadius: 2,
                        border: `1.5px solid ${MS_COLORS.red}`,
                        mb: 3,
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: MS_COLORS.red, mb: 0.5 }}>
                        Danger Zone
                    </Typography>
                    <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mb: 2 }}>
                        Cancelling this subscription is permanent. The donor, campaign owner, and organisation admin
                        will be notified by email.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setUnsubOpen(true)}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: MS_COLORS.red,
                            color: MS_COLORS.white,
                            '&:hover': { backgroundColor: MS_COLORS.red, opacity: 0.88 },
                        }}
                    >
                        Unsubscribe Donor
                    </Button>
                </Box>
            )}

            {/* Mark Failed Dialog */}
            <Dialog
                open={markFailedOpen}
                onClose={() => !markFailedLoading && setMarkFailedOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
                    Mark Transaction as Failed
                </DialogTitle>
                <Divider sx={{ mb: 2 }} />
                <DialogContent>
                    <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
                        This will mark the most recent non-failed transaction for this subscription as <strong>Failed</strong>.
                        {mostRecentTx?.donor_email
                            ? ` A failure notification will be sent to ${mostRecentTx.donor_email}.`
                            : ''}
                    </Typography>
                </DialogContent>
                <Divider sx={{ mt: 2 }} />
                <DialogActions sx={{ pt: 2, px: 3, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => setMarkFailedOpen(false)}
                        disabled={markFailedLoading}
                        variant="outlined"
                        sx={{ textTransform: 'none', borderRadius: '4px', borderColor: MS_COLORS.border, color: MS_COLORS.text }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMarkFailed}
                        disabled={markFailedLoading}
                        variant="contained"
                        sx={{ textTransform: 'none', borderRadius: '4px', backgroundColor: MS_COLORS.red, color: MS_COLORS.white, minWidth: 140, '&:hover': { backgroundColor: MS_COLORS.red, opacity: 0.9 }, '&:disabled': { backgroundColor: MS_COLORS.border } }}
                    >
                        {markFailedLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: MS_COLORS.white }} />Processing...</> : 'Mark as Failed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Retry Dialog */}
            <Dialog
                open={retryOpen}
                onClose={() => !retryLoading && setRetryOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
                    Retry Subscription
                </DialogTitle>
                <Divider sx={{ mb: 2 }} />
                <DialogContent>
                    <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
                        This will reset the subscription to <strong>active</strong> and clear the failed attempt counter.
                        The next scheduled charge will process normally on the next charge date.
                    </Typography>
                    <Box sx={{ mt: 1.5, backgroundColor: MS_COLORS.backgroundLight, p: 2, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
                            Donor: {subscription.donor_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                            Campaign: {subscription.campaign_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                            Next charge: {fmt(subscription.next_charge_date)}
                        </Typography>
                    </Box>
                </DialogContent>
                <Divider sx={{ mt: 2 }} />
                <DialogActions sx={{ pt: 2, px: 3, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => setRetryOpen(false)}
                        disabled={retryLoading}
                        variant="outlined"
                        sx={{ textTransform: 'none', borderRadius: '4px', borderColor: MS_COLORS.border, color: MS_COLORS.text }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRetry}
                        disabled={retryLoading}
                        variant="contained"
                        sx={{ textTransform: 'none', borderRadius: '4px', backgroundColor: MS_COLORS.primary, color: MS_COLORS.white, minWidth: 130, '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9 }, '&:disabled': { backgroundColor: MS_COLORS.border } }}
                    >
                        {retryLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: MS_COLORS.white }} />Retrying...</> : 'Confirm Retry'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unsubscribe Dialog */}
            <Dialog
                open={unsubOpen}
                onClose={() => !unsubLoading && setUnsubOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.red }}>
                    Unsubscribe Donor
                </DialogTitle>
                <Divider sx={{ mb: 2 }} />
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: MS_COLORS.textSecondary }}>
                        This will permanently cancel the recurring donation. The following parties will be notified by email:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: MS_COLORS.textSecondary, fontSize: '0.875rem', mt: 0 }}>
                        <li>Donor ({subscription.donor_email})</li>
                        <li>Campaign owner</li>
                        {subscription.owner_type === 'Organization' && <li>Organisation admin</li>}
                    </Box>
                    <Box sx={{ mt: 2, backgroundColor: '#FEF0EB', p: 2, borderRadius: 1, border: `1px solid ${MS_COLORS.red}30` }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: MS_COLORS.red }}>
                            This action cannot be undone.
                        </Typography>
                    </Box>
                </DialogContent>
                <Divider sx={{ mt: 2 }} />
                <DialogActions sx={{ pt: 2, px: 3, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => setUnsubOpen(false)}
                        disabled={unsubLoading}
                        variant="outlined"
                        sx={{ textTransform: 'none', borderRadius: '4px', borderColor: MS_COLORS.border, color: MS_COLORS.text }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUnsubscribe}
                        disabled={unsubLoading}
                        variant="contained"
                        sx={{ textTransform: 'none', borderRadius: '4px', backgroundColor: MS_COLORS.red, color: MS_COLORS.white, minWidth: 150, '&:hover': { backgroundColor: MS_COLORS.red, opacity: 0.9 }, '&:disabled': { backgroundColor: MS_COLORS.border } }}
                    >
                        {unsubLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: MS_COLORS.white }} />Cancelling...</> : 'Confirm Unsubscribe'}
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default RecurringDonationDetails;
